import * as THREE from 'three'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise } from '../shaderChunks'

/**
 * Ferrofluid: a chrome blob that grows spikes with the bass (like magnetic
 * fluid). Raymarched (sphere-traced) sphere whose radius is pushed out by ridged
 * angular noise; the spike amplitude rises with the bass/beat and the field
 * swirls on the energy clock. Shaded as reflective metal.
 */
export class Ferro extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    const half = new THREE.Vector3(5.5, 5.5, 5.5)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.NormalBlending,
      vertexShader: /* glsl */ `
        varying vec3 vWorld;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorld = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uEnergyTime, uBass, uBeat, uBeatPulse, uLevel, uReactivity, uBassPunch, uHue;
        uniform vec3 uBoxHalf;
        varying vec3 vWorld;
        ${snoise}
        ${hsv2rgb}
        float ridged(vec3 p) {
          float s = 0.0, a = 0.5;
          for (int i = 0; i < 4; i++) {
            float n = 1.0 - abs(snoise(p));
            s += a * n * n;
            p *= 2.1;
            a *= 0.5;
          }
          return s;
        }
        float mapF(vec3 p) {
          float baseR = 2.0;
          vec3 dir = normalize(p + 1e-5);
          float spike = ridged(dir * 3.5 + uEnergyTime * 0.25);
          float amp = 0.25 + uBass * uBassPunch * 2.4 * uReactivity + uBeatPulse * 1.2 * uReactivity;
          return length(p) - (baseR + spike * amp);
        }
        vec3 calcNormal(vec3 p) {
          vec2 e = vec2(0.02, 0.0);
          return normalize(vec3(
            mapF(p + e.xyy) - mapF(p - e.xyy),
            mapF(p + e.yxy) - mapF(p - e.yxy),
            mapF(p + e.yyx) - mapF(p - e.yyx)
          ));
        }
        void main() {
          vec3 ro = cameraPosition;
          vec3 rd = normalize(vWorld - ro);
          vec3 t1 = (-uBoxHalf - ro) / rd;
          vec3 t2 = (uBoxHalf - ro) / rd;
          vec3 tmin = min(t1, t2), tmax = max(t1, t2);
          float tn = max(max(tmin.x, tmin.y), tmin.z);
          float tf = min(min(tmax.x, tmax.y), tmax.z);
          tn = max(tn, 0.0);
          if (tn >= tf) discard;
          float t = tn;
          bool hit = false;
          for (int i = 0; i < 80; i++) {
            vec3 p = ro + rd * t;
            float dm = mapF(p);
            if (dm < 0.01) { hit = true; break; }
            t += max(dm, 0.02) * 0.6;   // conservative (radius field isn't an exact SDF)
            if (t > tf) break;
          }
          if (!hit) discard;
          vec3 p = ro + rd * t;
          vec3 n = calcNormal(p);
          vec3 v = -rd;
          float fres = pow(1.0 - max(dot(n, v), 0.0), 3.0);
          vec3 refl = reflect(rd, n);
          vec3 env = mix(vec3(0.04, 0.04, 0.07), hsv2rgb(vec3(uHue, 0.55, 0.95)), refl.y * 0.5 + 0.5);
          vec3 base = hsv2rgb(vec3(uHue, 0.3, 0.45));
          vec3 L = normalize(vec3(0.5, 1.0, 0.4));
          float spec = pow(max(dot(reflect(-L, n), v), 0.0), 50.0);
          vec3 col = mix(base, env, 0.6) + fres * 0.7 + spec * (1.0 + uBeatPulse * 0.6);
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    })
    this.material.uniforms.uBoxHalf = { value: half }
    this.object3D = new THREE.Mesh(new THREE.BoxGeometry(half.x * 2, half.y * 2, half.z * 2), this.material)
    this.object3D.frustumCulled = false
  }

  rebuild(): void {}
}
